/**
 * 세미나 목록 카드·상세가 함께 쓰는 표시 규칙 (docs/seminar-format-spec.md 3절, docs/seminar-coupon-spec.md "슬롯 고정형").
 *
 * - 일정 줄(scheduleLabel)·장소 줄(placeLabel)은 서버가 만들어 내려준다. 아직 안 내려오는 응답(구 서버·구 캐시)은
 *   여기서 같은 형식으로 만들어 쓴다: `10/15(목)` · `10/13(화) ~ 11/3(화)` · `… · 4회` · `… 시청` / `서울 강남` · `라이브` · `온라인`.
 * - 배지는 오른쪽 위 상태 하나만(폐강 > 판매 종료 > 모집마감 > 마감임박, 일반은 종료). 진행 방식·쿠폰은 본문·가격 줄과 겹쳐 두지 않는다.
 */

export const CAREER_COLOR: string = '#5A9B96';

/** 마감임박 — 모집 마감까지 남은 시간(3일) */
const CLOSING_SOON_MS: number = 3 * 24 * 60 * 60 * 1000;
/** 마감임박 — 정원 대비 점유 비율 */
const CLOSING_SOON_RATIO: number = 0.9;

const WEEKDAYS: string = '일월화수목금토';

/** 표시 규칙에 필요한 필드만 — 목록 요약(SeminarSummary)과 상세(Seminar) 둘 다 받는다 */
export interface SeminarDisplaySource {
    seminarType?: string;
    format?: string;
    schedule?: string;
    sessions?: Array<{ startDateTime: string; endDateTime: string; title?: string }>;
    isOnline?: boolean;
    scheduleLabel?: string;
    placeLabel?: string;
    /** null = 인원 제한 없음(정원 없는 상시 동영상) */
    remainingSeats?: number | null;
    startDateTime?: string;
    endDateTime?: string;
    recruitmentEndDateTime?: string;
    isRecruitmentClosed?: boolean;
    isFull?: boolean;
    status?: string;
    maxParticipants?: number;
    currentParticipants?: number;
    location?: unknown;
    address?: string;
    /** 상시 동영상 판매 종료 — 오른쪽 위 배지 "판매 종료" */
    salesClosed?: boolean;
    /** 폐강 — 오른쪽 위 배지 "폐강"(모집마감보다 먼저) */
    isCancelled?: boolean;
}

export const isCareerSeminar = (s: { seminarType?: string }): boolean => s.seminarType === 'CAREER';

const toDate = (value?: string): Date | null => {
    if (!value) return null;
    const date: Date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** `10/15(목)` */
export const formatMonthDay = (value?: string): string => {
    const date: Date | null = toDate(value);
    if (!date) return '';
    return `${date.getMonth() + 1}/${date.getDate()}(${WEEKDAYS[date.getDay()]})`;
};

/** `9/28(월) 23:10` — 올해가 아니면 앞에 연도(`2025. 9/28(일) 23:10`). 신청일·접속 시각처럼 날짜와 시각이 함께 필요한 곳 */
export const formatMonthDayClock = (value?: string, now: Date = new Date()): string => {
    const date: Date | null = toDate(value);
    if (!date) return '';
    const clock: string = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    const year: string = date.getFullYear() === now.getFullYear() ? '' : `${date.getFullYear()}. `;
    return `${year}${date.getMonth() + 1}/${date.getDate()}(${WEEKDAYS[date.getDay()]}) ${clock}`;
};

/** `14:00` */
export const formatClock = (value?: string): string => {
    const date: Date | null = toDate(value);
    if (!date) return '';
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const isSameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** 커리어만 진행 방식이 있다. 없으면 오프라인 */
export const formatOf = (s: SeminarDisplaySource): 'OFFLINE' | 'LIVE' | 'VOD' => {
    if (!isCareerSeminar(s)) return 'OFFLINE';
    return s.format === 'LIVE' || s.format === 'VOD' ? s.format : 'OFFLINE';
};

/** 일반 세미나의 '온라인' 체크 */
export const isGeneralOnline = (s: SeminarDisplaySource): boolean => !isCareerSeminar(s) && s.isOnline === true;

/** 하루짜리(당일)인지 — 시작·종료가 같은 날 */
export const isSingleDay = (s: SeminarDisplaySource): boolean => {
    const start: Date | null = toDate(s.startDateTime);
    const end: Date | null = toDate(s.endDateTime);
    if (!start) return true;
    return !end || isSameDay(start, end);
};

/** 동영상 상시 판매(OPEN) — 종료일이 없다(서버 endDateTime 은 자리값이라 화면에 쓰지 않는다) */
export const isOpenSchedule = (s: SeminarDisplaySource): boolean => formatOf(s) === 'VOD' && s.schedule === 'OPEN';

/** 상시 동영상 일정 줄 */
export const OPEN_SCHEDULE_LABEL: string = '상시 시청';

/** 목록·상세 일정 줄. 서버 scheduleLabel 우선, 없으면 시작·종료로 같은 형식을 만든다 */
export const buildScheduleLabel = (s: SeminarDisplaySource): string => {
    if (s.scheduleLabel) return s.scheduleLabel;
    if (isOpenSchedule(s)) return OPEN_SCHEDULE_LABEL;
    const start: string = formatMonthDay(s.startDateTime);
    if (!start) return '';
    if (isSingleDay(s)) return start;
    const range: string = `${start} ~ ${formatMonthDay(s.endDateTime)}`;
    if (isCareerSeminar(s) && s.schedule === 'SESSIONS' && s.sessions && s.sessions.length > 0) {
        return `${range} · ${s.sessions.length}회`;
    }
    if (formatOf(s) === 'VOD') return `${range} 시청`;
    return range;
};

const SIDO_SHORT: Record<string, string> = {
    서울특별시: '서울',
    부산광역시: '부산',
    대구광역시: '대구',
    인천광역시: '인천',
    광주광역시: '광주',
    대전광역시: '대전',
    울산광역시: '울산',
    세종특별자치시: '세종',
    경기도: '경기',
    강원도: '강원',
    강원특별자치도: '강원',
    충청북도: '충북',
    충청남도: '충남',
    전라북도: '전북',
    전북특별자치도: '전북',
    전라남도: '전남',
    전남광주통합특별시: '전남광주',
    경상북도: '경북',
    경상남도: '경남',
    제주특별자치도: '제주',
};

const addressOf = (s: SeminarDisplaySource): string => {
    const location: unknown = s.location;
    if (typeof location === 'string') return location;
    if (location && typeof location === 'object') {
        const loc = location as { address?: string; fullAddress?: string };
        return loc.address || loc.fullAddress || '';
    }
    return s.address || '';
};

/** `서울 관악구 조원로 25` → `서울 관악`, `경기 수원시 장안구 …` → `경기 수원` */
export const shortRegion = (address: string): string => {
    const tokens: string[] = address.trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return '';
    const sido: string = SIDO_SHORT[tokens[0]] ?? tokens[0];
    const second: string | undefined = tokens[1];
    if (!second || !/[시군구]$/.test(second)) return sido;
    const district: string = second.length > 2 ? second.slice(0, -1) : second;
    return `${sido} ${district}`;
};

/** 목록 장소 줄의 앞부분. 서버 placeLabel 우선, 없으면 진행 방식·온라인·주소로 만든다 */
export const buildPlaceLabel = (s: SeminarDisplaySource): string => {
    if (s.placeLabel) return s.placeLabel;
    const format = formatOf(s);
    if (format === 'LIVE') return '라이브';
    if (format === 'VOD') return '동영상';
    if (isGeneralOnline(s)) return '온라인';
    return shortRegion(addressOf(s));
};

/**
 * 정원 제한이 없는지 — 정원 없는 상시 동영상은 서버가 maxParticipants 10000(자리값)·remainingSeats null 을 준다.
 * 이때 자리 수는 어디에도 보이지 않는다.
 */
export const isUnlimitedSeats = (s: SeminarDisplaySource): boolean =>
    s.remainingSeats === null || (isOpenSchedule(s) && typeof s.remainingSeats !== 'number');

/** 남은 자리 — 서버 remainingSeats 우선, 없으면 정원 − 참가자 (정원 없거나 무제한이면 null) */
export const remainingSeatsOf = (s: SeminarDisplaySource): number | null => {
    if (typeof s.remainingSeats === 'number') return Math.max(0, s.remainingSeats);
    if (isUnlimitedSeats(s)) return null;
    const max: number = s.maxParticipants ?? 0;
    if (max <= 0) return null;
    return Math.max(0, max - (s.currentParticipants ?? 0));
};

export const isRecruitmentOver = (s: SeminarDisplaySource, now: number = Date.now()): boolean => {
    if (s.isRecruitmentClosed) return true;
    const end: Date | null = toDate(s.recruitmentEndDateTime);
    return end ? end.getTime() < now : false;
};

export type StatusBadgeTone = 'closed' | 'soon' | 'ended';

/**
 * 오른쪽 위 상태 배지 — 하나만.
 * 커리어: 모집마감(모집 종료·정원 마감·세미나 종료) > 마감임박(모집 마감 3일 전 또는 정원 90% 이상).
 * 일반: 종료(종료일이 지남)만.
 */
export const statusBadgeOf = (
    s: SeminarDisplaySource,
    now: number = Date.now(),
): { text: string; tone: StatusBadgeTone } | null => {
    // 폐강은 어느 상태보다 먼저(강사 페이지처럼 폐강 세미나도 목록에 남는 곳)
    if (s.isCancelled === true || s.status === 'CANCELLED') return { text: '폐강', tone: 'ended' };
    if (!isCareerSeminar(s)) {
        const end: Date | null = toDate(s.endDateTime) ?? toDate(s.startDateTime);
        return end && end.getTime() < now ? { text: '종료', tone: 'ended' } : null;
    }
    if (s.salesClosed) return { text: '판매 종료', tone: 'closed' };
    const remaining: number | null = remainingSeatsOf(s);
    const closed: boolean =
        isRecruitmentOver(s, now) ||
        s.isFull === true ||
        s.status === 'FULL' ||
        s.status === 'COMPLETED' ||
        remaining === 0;
    if (closed) return { text: '모집마감', tone: 'closed' };

    const recruitEnd: Date | null = toDate(s.recruitmentEndDateTime);
    const closingByDate: boolean = recruitEnd ? recruitEnd.getTime() - now <= CLOSING_SOON_MS : false;
    const max: number = s.maxParticipants ?? 0;
    const closingBySeats: boolean = max > 0 && remaining !== null && (max - remaining) / max >= CLOSING_SOON_RATIO;
    return closingByDate || closingBySeats ? { text: '마감임박', tone: 'soon' } : null;
};

export const STATUS_BADGE_COLOR: Record<StatusBadgeTone, string> = {
    closed: '#dc2626',
    soon: '#b45309',
    ended: '#6b7280',
};

/**
 * 동영상 환불 안내 줄(문장마다 한 줄) — 상세 안내·결제 모달·취소 팝업 맨 아래에 붙인다.
 * 동영상은 접속 링크를 열면 환불할 수 없다. 상시(OPEN)는 결제 후 7일 이내·접속 전에만 전액 환불.
 */
export const vodRefundNotes = (s: SeminarDisplaySource): string[] => {
    if (formatOf(s) !== 'VOD') return [];
    return isOpenSchedule(s)
        ? ['결제 후 7일 이내이고 접속 링크를 열기 전이면 전액 환불돼요.', '접속 링크를 열면 환불할 수 없어요.']
        : ['접속 링크를 열면 환불할 수 없어요.'];
};

/** `40,000원` / `무료` */
export const formatFee = (fee?: number): string => {
    const value: number = fee ?? 0;
    return value === 0 ? '무료' : `${value.toLocaleString()}원`;
};

/** 커리어 우선 정렬(안정 정렬) — 서버 정렬 파라미터가 없을 때 한 페이지 안에서만 쓴다 */
export const sortCareerFirst = <T extends { seminarType?: string }>(list: T[]): T[] =>
    list
        .map((item: T, index: number) => ({ item, index }))
        .sort((a, b) => {
            const diff: number = Number(isCareerSeminar(b.item)) - Number(isCareerSeminar(a.item));
            return diff !== 0 ? diff : a.index - b.index;
        })
        .map(({ item }) => item);
